import React, { useEffect, useRef, useState } from 'react';
import { Router } from 'react-router-dom';
import cx from 'classnames';
import { isRtlLanguage } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';

import {
  AccountManager,
  Bus,
  Fuse,
  PreferencesProvider,
  sessionId,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import { Button, Label, Loader } from '@atlas/ui';

import {
  ACCOUNT_STATUSES,
  CXFROM_STORAGE_KEY,
  POPUP_SIGN_UP_DELAY_STORAGE_KEYS,
} from 'common/constants';
import CustomStylesPortal from 'components/CustomStylesPortal/CustomStylesPortal';
import LayoutErrorFallback from 'components/Layout/LayoutErrorFallback';
import LiveChatView from 'components/LiveChat/LiveChatView';
import updateMeta from 'components/SeoPage/updateMeta';
import SingleSignUp from 'components/SignUp/SingleSignUp';
import { usePostHog } from 'hooks/usePostHog';
import useThemeBootstrap from 'hooks/useThemeBootstrap';
import DebugInfo from 'src/components/DebugInfo/DebugInfo';
import history from 'src/components/Layout/history';

import Header from './Header';

const SignUpRedirect = () => {
  const [linkToRedirect, setLinkToRedirect] = useState(null);
  const [hasBonusPopup, setShowBonusPopup] = useState(null);

  useEffect(() => {
    Bus.on(Bus.events.auth.simpleLogin, ({ isShowBonusPopup, redirectLink, sessionToken } = {}) => {
      sessionToken && AccountManager.setToken(sessionToken);
      redirectLink && setLinkToRedirect(redirectLink);
      isShowBonusPopup && setShowBonusPopup(isShowBonusPopup);
      if ((sessionToken && linkToRedirect) || (redirectLink && AccountManager.getToken())) {
        if (isShowBonusPopup || hasBonusPopup) {
          StorageManager.setItem(POPUP_SIGN_UP_DELAY_STORAGE_KEYS.BONUSES, true);
        }

        window.location = redirectLink || linkToRedirect;
      }
    });
  }, [hasBonusPopup, linkToRedirect]);

  return null;
};

const SignUpLayout = ({ optimized = false }) => {
  const headerRef = useRef(null);

  const { lang } = useSettings();
  const { authenticated, resolved, account } = useApplicationState();
  const params = new URLSearchParams(window.location.search);
  const cxfrom = params.get('cxfrom');

  const { init, login } = usePostHog();

  useEffect(() => {
    init().then(() => {
      login(sessionId);
    });
  }, [init, login]);

  useThemeBootstrap();

  useEffect(() => {
    updateMeta();
  }, []);

  useEffect(() => {
    const isRtl = isRtlLanguage({ lang });
    document.body.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  }, [lang]);

  useEffect(() => {
    if (resolved && authenticated && account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      window.location.href = cxfrom || '/';
    }
  }, [authenticated, cxfrom, resolved, account.status]);

  useEffect(() => {
    if (!cxfrom) return;

    StorageManager.setItem(CXFROM_STORAGE_KEY, cxfrom);
  }, []);

  const isShowRegForm = SiteConfigManager.getConfig('config.config.enable_registration');

  const layoutClassName = cx('layout', 'signup-layout', {
    'signup-layout-not-found': !isShowRegForm,
  });

  const layoutContent = (
    <PreferencesProvider>
      <div className={layoutClassName}>
        {isShowRegForm && (
          <>
            <SignUpRedirect />
            <SingleSignUp optimized={optimized} />
          </>
        )}
        {!isShowRegForm && (
          <>
            <div className="signup-layout-description">
              <Label className="title-text" message="Page not found" />
              <Label
                className="sub-title-text"
                message="The page you are looking for may have been deleted"
              />
            </div>
            <div className="signup-layout-buttons">
              <a className="primary" href={staticRoutes.sportsbook}>
                <Button label="GO To sport" size="big" type="primary" />
              </a>
              <a className="primary" href={staticRoutes.casino}>
                <Button label="Go to Casino" size="big" type="primary" />
              </a>
            </div>
          </>
        )}
        <DebugInfo />
      </div>
    </PreferencesProvider>
  );

  if (!resolved) {
    return <Loader className="layout-loader" height={60} width={60} />;
  }

  return (
    <Fuse fallback={LayoutErrorFallback}>
      <Router history={history}>
        <CustomStylesPortal />
        <Header ref={headerRef} />
        <main className="page">{layoutContent}</main>

        <LiveChatView />
      </Router>
    </Fuse>
  );
};

SignUpLayout.propTypes = {
  optimized: PropTypes.bool,
};

export default SignUpLayout;
